import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { TAppThunk } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import {
  AuthenticationTokenCreateEmpty,
  type TAuthenticationToken,
} from 'types/authenticationToken.type';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import api from '../services/api.service';

type TAuthenticationTokenState = {
  loading: boolean;
  hasErrors: boolean;
  selectedAuthenticationToken: TAuthenticationToken;
  allAuthenticationTokens: TAuthenticationToken[];
};

export const initialState: TAuthenticationTokenState = {
  loading: false,
  hasErrors: false,
  selectedAuthenticationToken: AuthenticationTokenCreateEmpty.createEmpty(),
  allAuthenticationTokens: [],
};

// Slice
const slice = createSlice({
  name: 'authenticationTokens',
  initialState,
  reducers: {
    setAuthenticationTokens: (
      state,
      { payload: authTokens }: PayloadAction<TAuthenticationToken[]>
    ) => {
      state.allAuthenticationTokens = authTokens;
    },
    deleteAuthenticationTokenRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteAuthenticationTokenSuccess: (
      state,
      { payload: authToken }: PayloadAction<TAuthenticationToken>
    ) => {
      state.allAuthenticationTokens = state.allAuthenticationTokens.filter(
        (authenticationToken: TAuthenticationToken) =>
          authenticationToken.id !== authToken.id
      );
      state.selectedAuthenticationToken =
        AuthenticationTokenCreateEmpty.createEmpty();
      finishedLoadingSuccess(state);
    },
    deleteAuthenticationTokenFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
});

export default slice.reducer;

// Actions
export const {
  setAuthenticationTokens,
  deleteAuthenticationTokenRequest,
  deleteAuthenticationTokenSuccess,
  deleteAuthenticationTokenFailure,
} = slice.actions;

export const setSelectedAuthenticationTokens =
  (authenticationTokens: TAuthenticationToken[]): TAppThunk =>
  (dispatch) => {
    dispatch(setAuthenticationTokens(authenticationTokens));
  };

export const deleteAuthenticationToken = createAppAsyncThunk<
  void,
  TAuthenticationToken
>(
  'authenticationTokens/deleteAuthenticationToken',
  async (authenticationToken, { dispatch }) => {
    try {
      dispatch(deleteAuthenticationTokenRequest());
      await api.delete({
        endpoint: `/authentication-tokens/${authenticationToken.id}`,
      });
      dispatch(deleteAuthenticationTokenSuccess(authenticationToken as any));
    } catch (e) {
      dispatch(deleteAuthenticationTokenFailure());
      return console.error(e);
    }
  }
);
